import { sortByDate } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";

import { useResumes } from "@/client/services/resume";

import { BaseCard } from "./_components/base-card";
import { ResumeCard } from "./_components/resume-card";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { CaretDown, Plus, UploadSimple } from "@phosphor-icons/react";
import { t } from "@lingui/macro";

import { trackUploadClicked } from "@/client/libs/analytics";
import {
  ImportType,
  type ImportDialogPayloadItem,
} from "@/client/pages/dashboard/resumes/_dialogs/import";
import { useDialog } from "@/client/stores/dialog";
import type { ResumeDto } from "@reactive-resume/dto";

export const GridView = () => {
  const { resumes, loading } = useResumes();
  const createDialog = useDialog<ResumeDto>("resume");
  const importDialog = useDialog<ImportDialogPayloadItem>("import");
  const uploadDialog = useDialog("upload");

  return (
    <>
      {/* Action Buttons */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          size="md"
          variant="primary"
          className="flex items-center gap-2"
          onClick={() => createDialog.open("create")}
        >
          <Plus size={18} />
          <span>Create Resume</span>
        </Button>
        <div className="inline-flex rounded-md shadow-sm">
          <Button
            size="md"
            variant="secondary"
            className="flex items-center gap-2 rounded-r-none border-r border-secondary-foreground/25"
            onClick={() => {
              trackUploadClicked();
              uploadDialog.open("create");
            }}
          >
            <UploadSimple size={18} />
            <span>{t`Upload Resume`}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="md"
                variant="secondary"
                className="rounded-l-none px-2"
                aria-label={t`More import options`}
              >
                <CaretDown size={18} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <DropdownMenuItem
                onClick={() =>
                  importDialog.open("create", {
                    id: "import",
                    item: { initialImportType: ImportType["json-resume-json"] },
                  })
                }
              >
                {t`JSON Resume (.json)`}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => importDialog.open("create", { id: "import" })}
              >
                {t`Other import sources…`}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="duration-300 animate-in fade-in"
              style={{ animationFillMode: "backwards", animationDelay: `${i * 300}ms` }}
            >
              <BaseCard />
            </div>
          ))}

        {resumes && (
          <AnimatePresence>
            {resumes
              .sort((a, b) => sortByDate(a, b, "updatedAt"))
              .map((resume, index) => (
                <motion.div
                  key={resume.id}
                  layout
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0, transition: { delay: (index + 2) * 0.1 } }}
                  exit={{ opacity: 0, filter: "blur(8px)", transition: { duration: 0.5 } }}
                >
                  <ResumeCard resume={resume} />
                </motion.div>
              ))}
          </AnimatePresence>
        )}
      </div>
    </>
  );
};
